import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * BILLING-02 — One-time credits purchase success page.
 * Polls /billing/usage to show the updated credits_balance.
 */
export default function CreditsSuccessPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    (async () => {
      try {
        if (sessionId) {
          // Confirm payment + give webhook time to credit balance
          await api.getCheckoutStatus(sessionId);
        }
      } catch (e) {
        // Non-blocking — the webhook is the source of truth for balance.
        // eslint-disable-next-line no-console
        console.warn('[CreditsSuccess] checkout/status failed (non-blocking):', e);
      }

      // Poll usage up to 5×1s to catch webhook update
      for (let i = 0; i < 5; i++) {
        try {
          const u = await api.getUsageStats();
          setUsage(u);
          if ((u.credits_balance || 0) > 0) break;
        } catch (e) {
          // Keep polling on transient errors
          // eslint-disable-next-line no-console
          console.warn(`[CreditsSuccess] usage poll attempt ${i + 1} failed:`, e);
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      setLoading(false);
      toast.success('Créditos adicionados à sua conta!');
    })();
  }, [searchParams]);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="credits-success-page">
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Confirmando seus créditos...</p>
          </div>
        ) : (
          <>
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>

            <h1
              className={`text-3xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
              data-testid="credits-success-title"
            >
              Créditos Adicionados!
            </h1>

            <p className={`text-lg mb-8 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Suas consultas adicionais já estão disponíveis na sua conta.
            </p>

            <div
              className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'} rounded-xl border p-6 mb-8 inline-flex items-center gap-3`}
              data-testid="credits-success-balance"
            >
              <Sparkles className="h-6 w-6 text-teal-500" />
              <div>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Saldo atual de créditos</p>
                <p className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {usage?.credits_balance ?? 0} consultas
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/patients')}
                className="bg-primary hover:bg-primary-hover"
                data-testid="credits-success-new-consultation"
              >
                Iniciar nova consulta
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/billing')}
                data-testid="credits-success-see-plan"
              >
                Ver meu plano
              </Button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
